import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import CsvIcon from "src/core/assets/images/icons/csv.svg?react";
import ExportIcon from "src/core/assets/images/icons/export.svg?react";
import { PageBreadcrumb } from "src/core/components/layout/PageBreadcrumb";
import { Button, buttonVariants } from "src/core/components/ui/button";
import { Pagination } from "src/core/components/ui/pagination";
import { RadioGroup, RadioGroupItem } from "src/core/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/core/components/ui/table";
import { useImportFolders } from "src/core/hooks/import/useImportFolders";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { cn } from "src/core/lib/utils";
import {
  buildImportReportCsv,
  computeImportStats,
  downloadTextFile,
} from "src/core/utils/import/importReport";
import { paginateImportRows } from "src/core/utils/import/paginateImportRows";
import { templateCsv } from "src/core/utils/import/parseImportCsv";
import {
  AssignmentMode,
  IMPORT_MAX_ROWS,
  ImportRow,
  ImportRowStatus,
} from "src/core/utils/import/types";

type StatusFilter = "all" | ImportRowStatus;

const FILTERS: StatusFilter[] = ["all", "created", "existing", "error"];
const DEFAULT_PAGE_SIZE = 20;

export const ImportFolders = () => {
  const { t } = useTranslation();
  const paths = useRequiredTenantPaths();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    mode,
    setMode,
    modeLocked,
    rows,
    fileError,
    fileName,
    phase,
    processed,
    planned,
    loadFile,
    importFirstLine,
    importRest,
    cancel,
  } = useImportFolders();

  const stats = useMemo(() => computeImportStats(rows), [rows]);
  const visibleRows = rows.filter((row) => filter === "all" || row.status === filter);
  const pagination = useMemo(
    () => paginateImportRows(visibleRows, pageSize, pageIndex),
    [pageIndex, pageSize, visibleRows]
  );

  useEffect(() => {
    if (pagination.totalPages > 0 && pageIndex >= pagination.totalPages) {
      setPageIndex(pagination.totalPages - 1);
    }
  }, [pageIndex, pagination.totalPages]);
  const hasPendingValid = rows.some((row) => row.isValid && row.status === "pending");
  const hasResult = rows.some((row) => row.status !== "pending");
  const running = phase === "running";

  const formatMessage = (row: ImportRow) => {
    const parts = row.messageCodes.map((code) => t(`import.messages.${code}`));
    if (row.messageDetail) parts.push(row.messageDetail);
    return parts.join(" — ");
  };

  const formatStatus = (row: ImportRow) => t(`import.status.${row.status}`);

  const renderStep = (value: boolean | null) => {
    if (value === null) return null;
    return <StepBadge ok={value} label={value ? t("import.stepOk") : t("import.stepKo")} />;
  };

  const downloadReport = () => {
    const csv = buildImportReportCsv(rows, mode, {
      status: formatStatus,
      message: formatMessage,
    });
    const baseName = fileName?.replace(/\.csv$/i, "") ?? "import";
    downloadTextFile(`${baseName}-rapport.csv`, csv);
  };

  const downloadTemplate = () => {
    downloadTextFile(
      mode === "with" ? "modele-import-assignation.csv" : "modele-import.csv",
      templateCsv(mode)
    );
  };

  const filterLabel = (value: StatusFilter) =>
    t(
      value === "all"
        ? "import.filterAll"
        : value === "created"
        ? "import.filterCreated"
        : value === "existing"
        ? "import.filterExisting"
        : "import.filterError"
    );

  const statusClassName = (status: ImportRowStatus) => {
    if (status === "created") return "low-risk";
    if (status === "existing") return "normal-risk";
    if (status === "error") return "high-risk";
    return undefined;
  };

  return (
    <div className="flex flex-col">
      <PageBreadcrumb className="ml-40 mt-20 mb-5" />
      <div className="px-40">
        <div className="folders-tab-list">
          <div className="folders-tab !w-fit min-w-[180px] px-15" data-state="active">
            <div className="folders-tab-title">{t("import.title")}</div>
          </div>
        </div>

        <div className="folders-list-wrapper">
          <div className="ml-15 mb-20 flex flex-row flex-wrap items-center gap-x-30 gap-y-10">
            <label className={cn("inline-flex", running && "pointer-events-none opacity-50")}>
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                disabled={running}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void loadFile(file);
                  event.target.value = "";
                }}
              />
              <span className={cn(buttonVariants({ variant: "upload" }), "cursor-pointer")}>
                <CsvIcon className="text-input" />
                {t("import.chooseFile")}
              </span>
            </label>
            <Button type="button" variant="outline" onClick={downloadTemplate} disabled={running}>
              {t("import.template")}
            </Button>
            {fileName && (
              <span className="font-light">{t("import.fileName", { name: fileName })}</span>
            )}
          </div>

          {fileError && (
            <p className="ml-15 mb-20 font-light text-alert">
              {t(`import.fileError.${fileError}`, { max: IMPORT_MAX_ROWS })}
            </p>
          )}

          <div className="ml-15 mb-20 flex flex-row flex-wrap items-center gap-x-30 gap-y-10">
            <RadioGroup
              value={mode}
              onValueChange={(value) => setMode(value as AssignmentMode)}
              disabled={modeLocked || running}
              className="flex flex-row items-center gap-x-30"
            >
              <div className="flex items-center gap-x-10">
                <RadioGroupItem value="with" id="import-mode-with" />
                <label htmlFor="import-mode-with" className="font-light">
                  {t("import.modeWith")}
                </label>
              </div>
              <div className="flex items-center gap-x-10">
                <RadioGroupItem value="without" id="import-mode-without" />
                <label htmlFor="import-mode-without" className="font-light">
                  {t("import.modeWithout")}
                </label>
              </div>
            </RadioGroup>

            <Button
              type="button"
              onClick={importFirstLine}
              disabled={running || Boolean(fileError) || phase !== "idle" || !hasPendingValid}
            >
              {t("import.importFirst")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={importRest}
              disabled={
                running ||
                Boolean(fileError) ||
                (phase !== "piloted" && phase !== "stopped") ||
                !hasPendingValid
              }
            >
              {t("import.importRest")}
            </Button>
            {running && (
              <Button type="button" variant="cancel" onClick={cancel}>
                {t("import.cancel")}
              </Button>
            )}
          </div>

          <div className="ml-15 mb-10 flex flex-wrap items-center gap-x-30 gap-y-10">
            <Stat value={t("import.statsCreated", { pct: stats.createdPct })} tone="created" />
            <Stat value={t("import.statsExisting", { pct: stats.existingPct })} tone="existing" />
            <Stat value={t("import.statsError", { pct: stats.errorPct })} tone="error" />
          </div>

          <div className="ml-15 mb-20 flex items-center gap-x-30">
            {FILTERS.map((value) => (
              <button
                key={value}
                type="button"
                className={cn(
                  "h-30 font-medium",
                  filter === value ? "border-b-2 border-tabs-border" : "opacity-50"
                )}
                onClick={() => {
                  setFilter(value);
                  setPageIndex(0);
                }}
              >
                {filterLabel(value)}
              </button>
            ))}
          </div>

          <div className="mb-10 flex items-center gap-x-20">
            {planned > 0 ? (
              <div className="flex min-w-0 flex-1 items-center gap-x-15">
                <div className="h-10 min-w-0 flex-1 overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full bg-primary"
                    style={{
                      width: `${planned === 0 ? 0 : Math.round((processed / planned) * 100)}%`,
                    }}
                  />
                </div>
                <div className="shrink-0 font-light">
                  {t("import.progress", { done: processed, total: planned })}
                </div>
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <Button
              type="button"
              className="shrink-0"
              onClick={downloadReport}
              disabled={!hasResult}
            >
              <ExportIcon />
              {t("import.downloadReport")}
            </Button>
          </div>

          <Table
            className="folders-list-table import-folders-table min-w-[992px]"
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <TableHeader className="folders-list-header">
              <TableRow>
                <TableHead className="w-[5%]">{t("import.columns.line")}</TableHead>
                <TableHead className="w-[9%]">{t("import.columns.siren")}</TableHead>
                <TableHead className="w-[8%]">{t("import.columns.entityType")}</TableHead>
                <TableHead className="w-[9%]">{t("import.columns.status")}</TableHead>
                <TableHead className="w-[8%]">{t("import.columns.existant")}</TableHead>
                <TableHead className="w-[11%]">{t("import.columns.creationAuto")}</TableHead>
                <TableHead className="w-[8%]">{t("import.columns.creationFolder")}</TableHead>
                <TableHead className="w-[11%]">{t("import.columns.ereputation")}</TableHead>
                {mode === "with" && (
                  <TableHead className="w-[10%]">{t("import.columns.follow")}</TableHead>
                )}
                <TableHead>{t("import.columns.message")}</TableHead>
                <TableHead className="w-[7%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.pageRows.length === 0 ? (
                <TableRow className="import-folders-empty-row">
                  <TableCell
                    colSpan={mode === "with" ? 11 : 10}
                    className="!whitespace-normal text-center"
                  >
                    {t("import.emptyTable")}
                  </TableCell>
                </TableRow>
              ) : (
                pagination.pageRows.map((row) => {
                  return (
                    <TableRow className="folders-list-table-row" key={row.lineNumber}>
                      <TableCell className="folders-list-table-cell">{row.lineNumber}</TableCell>
                      <TableCell className="folders-list-table-cell">{row.siren}</TableCell>
                      <TableCell className="folders-list-table-cell">{row.entityType}</TableCell>
                      <TableCell
                        className={cn("folders-list-table-cell", statusClassName(row.status))}
                      >
                        {formatStatus(row)}
                      </TableCell>
                      <TableCell className="folders-list-table-cell">
                        {row.existant === null
                          ? ""
                          : row.existant
                          ? t("import.yes")
                          : t("import.no")}
                      </TableCell>
                      <TableCell className="folders-list-table-cell">
                        {renderStep(row.creationAuto)}
                      </TableCell>
                      <TableCell className="folders-list-table-cell">
                        {renderStep(row.creationFolder)}
                      </TableCell>
                      <TableCell className="folders-list-table-cell">
                        {renderStep(row.callEReputation)}
                      </TableCell>
                      {mode === "with" && (
                        <TableCell className="folders-list-table-cell">
                          {renderStep(row.callFollowFolder)}
                        </TableCell>
                      )}
                      <TableCell className="folders-list-table-cell">
                        {formatMessage(row)}
                      </TableCell>
                      <TableCell className="folders-list-table-cell">
                        {row.folderFileId && (
                          <Link
                            to={paths.folder(row.folderFileId)}
                            className="text-primary no-underline hover:underline"
                          >
                            {t("import.openFolder")}
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {pagination.totalCount > 0 && (
            <Pagination
              currentPage={Math.min(pageIndex, Math.max(pagination.totalPages - 1, 0))}
              totalPages={pagination.totalPages}
              onPageChange={setPageIndex}
              pageSize={pageSize}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPageIndex(0);
              }}
              totalCount={pagination.totalCount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const StepBadge = ({ ok, label }: { ok: boolean; label: string }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-lg px-10 py-1 font-medium text-white",
      ok ? "bg-success" : "bg-alert"
    )}
  >
    {label}
  </span>
);

const Stat = ({ value, tone }: { value: string; tone: "created" | "existing" | "error" }) => (
  <div
    className={cn(
      "small-card font-medium",
      tone === "created" && "low-risk",
      tone === "existing" && "normal-risk",
      tone === "error" && "high-risk"
    )}
  >
    {value}
  </div>
);
